import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationApi } from '../../services/api';
import { PageHeader, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  BoltIcon,
  ArrowPathIcon,
  LinkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const INTEGRATIONS = [
  { type: 'jira',             name: 'Jira',              color: 'bg-blue-600',    hint: '{ "base_url": "https://your.atlassian.net", "email": "you@co.com", "api_token": "…" }' },
  { type: 'github',           name: 'GitHub',            color: 'bg-gray-900',    hint: '{ "org": "your-org", "token": "ghp_…" }' },
  { type: 'gitlab',           name: 'GitLab',            color: 'bg-orange-600',  hint: '{ "base_url": "https://gitlab.com", "token": "glpat-…" }' },
  { type: 'salesforce',       name: 'Salesforce',        color: 'bg-sky-600',     hint: '{ "instance_url": "https://…my.salesforce.com", "client_id": "…", "client_secret": "…" }' },
  { type: 'hubspot',          name: 'HubSpot',           color: 'bg-orange-500',  hint: '{ "api_key": "pat-na1-…" }' },
  { type: 'bamboohr',         name: 'BambooHR',          color: 'bg-green-600',   hint: '{ "subdomain": "your-co", "api_key": "…" }' },
  { type: 'rippling',         name: 'Rippling',          color: 'bg-yellow-500',  hint: '{ "api_key": "…" }' },
  { type: 'workday',          name: 'Workday',           color: 'bg-amber-700',   hint: '{ "tenant": "…", "client_id": "…", "client_secret": "…" }' },
  { type: 'slack',            name: 'Slack',             color: 'bg-purple-600',  hint: '{ "bot_token": "xoxb-…", "signing_secret": "…" }' },
  { type: 'teams',            name: 'Microsoft Teams',   color: 'bg-indigo-600',  hint: '{ "tenant_id": "…", "client_id": "…", "client_secret": "…" }' },
  { type: 'google_workspace', name: 'Google Workspace',  color: 'bg-red-500',     hint: '{ "service_account_json": { … } }' },
  { type: 'linkedin_learning',name: 'LinkedIn Learning', color: 'bg-blue-700',    hint: '{ "client_id": "…", "client_secret": "…" }' },
];

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function ReliabilityBar({ value }) {
  const pct = Math.max(0, Math.min(1, Number(value) || 0)) * 100;
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={clsx('h-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-10 text-right">{Math.round(pct)}%</span>
    </div>
  );
}

function ConnectForm({ meta, saving, onClose, onSubmit }) {
  const [raw, setRaw] = useState(meta.hint);
  const [error, setError] = useState('');

  const submit = () => {
    try {
      const parsed = JSON.parse(raw);
      setError('');
      onSubmit(parsed);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
        <InformationCircleIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">Provide credentials as JSON. Store tokens in your secrets manager — CalibZa encrypts them at rest but never exposes them back in the UI.</p>
      </div>
      <div>
        <label className="label">Credentials (JSON)</label>
        <textarea className="input font-mono text-xs h-40 resize-none" value={raw} onChange={e => setRaw(e.target.value)} />
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={saving} onClick={submit} className="btn-primary disabled:opacity-40">{saving ? 'Connecting…' : `Connect ${meta.name}`}</button>
      </div>
    </div>
  );
}

export default function Integrations() {
  const qc = useQueryClient();
  const [connecting, setConnecting]   = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn:  () => integrationApi.list().then(r => r.data),
  });

  const connectMut = useMutation({
    mutationFn: ({ type, credentials }) => integrationApi.connect(type, { credentials, settings: {} }),
    onSuccess:  () => { toast.success('Integration connected'); qc.invalidateQueries(['integrations']); setConnecting(null); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to connect'),
  });

  const syncMut = useMutation({
    mutationFn: (type) => integrationApi.sync(type),
    onSuccess:  () => { toast.success('Sync queued'); qc.invalidateQueries(['integrations']); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Sync failed'),
  });

  const disconnectMut = useMutation({
    mutationFn: (type) => integrationApi.disconnect(type),
    onSuccess:  () => { toast.success('Integration disconnected'); qc.invalidateQueries(['integrations']); setDisconnecting(null); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to disconnect'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const byType = new Map((data || []).map(i => [i.type, i]));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader title="Integrations" subtitle="Connect the tools your teams use so signal flows into the scoring engine" />

      <div className="card p-4 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 flex items-start gap-3">
        <BoltIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">Tool data feeds the IP (Individual Performance) component of the score.</p>
          <p className="text-blue-800 mt-0.5">Higher reliability = more weight. Keep integrations fresh and authenticated for the best calibration outcomes.</p>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {['Integration','Status','Reliability','Last synced','Actions'].map(h => <th key={h} className="th">{h}</th>)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {INTEGRATIONS.map(meta => {
              const conn = byType.get(meta.type);
              const connected = !!(conn && conn.is_active);
              return (
                <tr key={meta.type} className="tr-hover">
                  <td className="td">
                    <div className="flex items-center gap-3">
                      <div className={clsx('w-9 h-9 rounded-lg text-white flex items-center justify-center text-sm font-bold shrink-0', meta.color)}>
                        {meta.name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{meta.name}</p>
                        <p className="text-xs text-gray-400">{meta.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="td">
                    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                      {connected ? 'Connected' : 'Not connected'}
                    </span>
                  </td>
                  <td className="td">
                    {conn ? <ReliabilityBar value={conn.reliability_score} /> : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="td text-xs text-gray-500">{conn ? relativeTime(conn.last_synced_at) : '—'}</td>
                  <td className="td">
                    {connected ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => syncMut.mutate(meta.type)} disabled={syncMut.isPending} className="btn-secondary btn-sm disabled:opacity-40">
                          <ArrowPathIcon className="w-3.5 h-3.5" /> Sync now
                        </button>
                        <button onClick={() => setDisconnecting(meta)} className="btn-danger btn-sm">Disconnect</button>
                      </div>
                    ) : (
                      <button onClick={() => setConnecting(meta)} className="btn-primary btn-sm">
                        <LinkIcon className="w-3.5 h-3.5" /> Connect
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={!!connecting} onClose={() => setConnecting(null)} title={connecting ? `Connect ${connecting.name}` : ''} size="md">
        {connecting && (
          <ConnectForm
            meta={connecting}
            saving={connectMut.isPending}
            onClose={() => setConnecting(null)}
            onSubmit={(credentials) => connectMut.mutate({ type: connecting.type, credentials })}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!disconnecting}
        onClose={() => setDisconnecting(null)}
        onConfirm={() => disconnecting && disconnectMut.mutate(disconnecting.type)}
        title={disconnecting ? `Disconnect ${disconnecting.name}?` : ''}
        message="Disconnecting stops data sync immediately. Historical signals remain in the scoring engine until the next recompute."
        danger
      />
    </div>
  );
}
